/**
 * HTTP client for the AMATL FastAPI backend.
 *
 * In dev, Vite proxies `/api/*` to :8000 (see vite.config.ts).
 * In prod, FastAPI serves both `/api/*` and the SPA from the same origin,
 * so relative URLs work everywhere.
 */
import type { Message, Operation, Part, ToolDefinition } from './store'

export type HealthStatus = {
  status: string
  api_version: string
  phase: string
}

export type WeldingConfig = {
  bead_width_mm: number
  bead_overlap_pct: number
  bead_height_mm: number
  travel_mm_min: number
  stickout_mm: number
  wire_dia_mm: number
  current_a: number
  voltage_v: number
  wire_feed_m_min: number
  layers: number
  radial_passes: number
  samples_per_rev: number
  lead_angle_deg: number
  torch_offset_mm: number
  emit_arc: boolean
  worm_axial_len_mm: number
  worm_r_min_mm: number
  worm_r_max_mm: number
  // Phase 2D: per-operation tilt angle for a 2-axis positioner.
  // null = inherit the cell default; number = override. Lets "top of
  // flight" and "bottom of flight" ops share one RAPID module.
  positioner_tilt_deg?: number | null
}

export type GenerateResult = {
  toolpath: unknown
  trajectory: unknown | null
  rapid: string | null
  collision: { collisionCount: number; firstFrame?: number } | null
  frameCount: number
  messages: Message[]
}

async function getJson<T>(path: string): Promise<T> {
  const res = await fetch(path, { headers: { accept: 'application/json' } })
  if (!res.ok) throw new Error(`${res.status} ${res.statusText} — ${path}`)
  return res.json() as Promise<T>
}

async function postJson<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(path, {
    method: 'POST',
    headers: { 'content-type': 'application/json', accept: 'application/json' },
    body: JSON.stringify(body),
  })
  if (!res.ok) throw new Error(`${res.status} ${res.statusText} — ${path}`)
  return res.json() as Promise<T>
}

async function patchJson<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(path, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json', accept: 'application/json' },
    body: JSON.stringify(body),
  })
  if (!res.ok) throw new Error(`${res.status} ${res.statusText} — ${path}`)
  return res.json() as Promise<T>
}

export function fetchHealth(): Promise<HealthStatus> {
  return getJson<HealthStatus>('/api/health')
}

export async function uploadStep(file: File): Promise<Part> {
  const fd = new FormData()
  fd.append('file', file)
  const res = await fetch('/api/upload-step', { method: 'POST', body: fd })
  if (!res.ok) throw new Error(`upload failed: ${res.status}`)
  return (await res.json()) as Part
}

export function generateOperation(
  uploadId: string,
  op: Operation,
  activeToolId?: string | null,
): Promise<GenerateResult> {
  return postJson<GenerateResult>('/api/generate', {
    upload_id: uploadId,
    operation: {
      id: op.id,
      kind: op.kind,
      faces: op.faces,
      seam_pairs: op.seamPairs,
      wps: op.wps,
    },
    active_tool_id: activeToolId ?? null,
  })
}

export function listTools(): Promise<ToolDefinition[]> {
  return getJson<ToolDefinition[]>('/api/tools')
}

// ---- Projects (batch workspaces) ----------------------------------------

export type ProjectSummary = {
  project_id: string
  name: string
  created_at: string
  robot: string
  postprocessor: string
  profile: string
  input_count: number
  cell_id?: string | null
}

export type ProjectDetail = ProjectSummary & {
  inputs: string[]
  outputs: string[]
  active_tool_id?: string | null
  cell_id?: string | null
}

export type PartRunStage = {
  stage: string
  ok: boolean
  detail: string
  duration_ms: number
}

export type PartRunResult = {
  step_filename: string
  part_id: string
  revision: string
  ok: boolean
  face_count: number
  selected_face_count: number
  waypoint_count: number
  collision_count: number
  rapid_bytes: number
  stages: PartRunStage[]
  summary_line: string
}

export type ProjectRunResult = {
  project_id: string
  started_at: string
  finished_at: string
  duration_s: number
  parts: PartRunResult[]
}

export function listProjects(): Promise<ProjectSummary[]> {
  return getJson<ProjectSummary[]>('/api/projects')
}

export function getProject(id: string): Promise<ProjectDetail> {
  return getJson<ProjectDetail>(`/api/projects/${id}`)
}

// ---- v0.6 templates ----------------------------------------------------

export type TemplateSummary = {
  template_id: string
  name: string
  description: string
  builtin: boolean
}

export function listTemplates(): Promise<TemplateSummary[]> {
  return getJson<TemplateSummary[]>('/api/templates')
}


// ---- v0.6 project cell + params ----------------------------------------
//
// Mirrors api.models.ProjectCellPayload / ProjectParamsPayload. Keeping
// the TypeScript shape in one place so the editor component and the
// property manager don't drift.

export type ProjectCellPayload = {
  robot: {
    robot_id: string
    position_mm: number[]
    orientation_deg: number[]
    tool_length_m: number
  }
  positioner: {
    model_id: string
    mech_unit_name: string
    base_position_mm: number[]
    base_orientation_deg: number[]
    first_axis_in_base: number[]
    faceplate_offset_mm: number[]
    collision_mesh: string | null
    activate_in_proc: boolean
    // Phase 2C: empty tilt_mech_unit_name = single-axis positioner.
    tilt_mech_unit_name: string
    tilt_external_axis_slot: string
    tilt_axis_in_base: number[]
    tilt_housing_offset_mm: number[]
    tilt_limits_deg: number[] | null
  }
  work_object: {
    offset_mm: number[]
    orientation_deg: number[]
    worm_axis_in_faceplate: number[]
  }
  active_tool_id: string | null
  joint_limits_deg: number[][] | null
  singularity_j5_threshold_deg: number
  extra_colliders: string[]
}

export type ProjectParamsPayload = {
  weld: {
    travel_speed_mm_s: number
    current_a: number
    voltage_v: number
    weave_amplitude_mm: number
    weave_frequency_hz: number
  }
  classification: {
    hardface_face_normal_tol_deg: number
    min_face_area_mm2: number
  }
  toolpath: {
    stepover_mm: number
    overlap_pct: number
  }
}

// ---- Auto-select (bulk face classifier) --------------------------------

export type AutoSelectResult = {
  profile: string
  included_faces: number[]
  excluded_faces: number[]
  reasons: {
    face_index: number
    surface_type: string
    area: number
    decision: 'include' | 'exclude'
    reason: string
  }[]
}

export function autoSelectFaces(
  uploadId: string,
  profile: 'worm' | 'auger' = 'worm',
): Promise<AutoSelectResult> {
  return postJson<AutoSelectResult>(
    `/api/upload-step/${uploadId}/auto-select?profile=${profile}`,
    {},
  )
}


export function getProjectCell(id: string): Promise<ProjectCellPayload> {
  return getJson<ProjectCellPayload>(`/api/projects/${id}/cell`)
}

export function patchProjectCell(
  id: string, payload: ProjectCellPayload,
): Promise<ProjectCellPayload> {
  return patchJson<ProjectCellPayload>(`/api/projects/${id}/cell`, payload)
}

export function getProjectParams(id: string): Promise<ProjectParamsPayload> {
  return getJson<ProjectParamsPayload>(`/api/projects/${id}/params`)
}

export function patchProjectParams(
  id: string, payload: ProjectParamsPayload,
): Promise<ProjectParamsPayload> {
  return patchJson<ProjectParamsPayload>(`/api/projects/${id}/params`, payload)
}

export function createProject(opts: {
  name: string
  robot: string
  postprocessor: string
  profile: 'worm' | 'auger'
  active_tool_id?: string | null
  cell_id?: string | null
  // v0.6: copy the named template's cell + params into the new
  // project. Takes priority over cell_id when both are passed.
  from_template?: string | null
}): Promise<ProjectSummary> {
  return postJson<ProjectSummary>('/api/projects', opts)
}

// ---- Cells (v0.5) --------------------------------------------------------

export type RobotPlacement = {
  robot_id: string
  position_mm: number[]
  orientation_deg: number[]
  tool_length_m: number
}

export type PositionerPlacement = {
  model_id: string
  mech_unit_name: string
  base_position_mm: number[]
  base_orientation_deg: number[]
  first_axis_in_base: number[]
  faceplate_offset_mm: number[]
  collision_mesh: string | null
  activate_in_proc: boolean
}

export type WorkObjectPlacement = {
  offset_mm: number[]
  orientation_deg: number[]
  worm_axis_in_faceplate: number[]
}

export type CellDefinition = {
  id: string
  name: string
  description: string
  version: number
  robot: RobotPlacement
  positioner: PositionerPlacement
  work_object: WorkObjectPlacement
  active_tool_id: string | null
  joint_limits_deg: number[][] | null
  singularity_j5_threshold_deg: number
  extra_colliders: string[]
}

export function listCells(): Promise<CellDefinition[]> {
  return getJson<CellDefinition[]>('/api/cells')
}

export function getCell(id: string): Promise<CellDefinition> {
  return getJson<CellDefinition>(`/api/cells/${id}`)
}

export function createCell(opts: {
  name: string
  description?: string
  robot?: Partial<RobotPlacement>
  positioner?: Partial<PositionerPlacement>
  work_object?: Partial<WorkObjectPlacement>
  active_tool_id?: string | null
}): Promise<CellDefinition> {
  return postJson<CellDefinition>('/api/cells', opts)
}

export async function updateCell(c: CellDefinition): Promise<CellDefinition> {
  const r = await fetch(`/api/cells/${c.id}`, {
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(c),
  })
  if (!r.ok) throw new Error(`${r.status} ${r.statusText}`)
  return r.json()
}

export async function deleteCell(id: string): Promise<void> {
  const r = await fetch(`/api/cells/${id}`, { method: 'DELETE' })
  if (!r.ok) throw new Error(`${r.status} ${r.statusText}`)
}

export async function deleteProject(id: string): Promise<void> {
  const r = await fetch(`/api/projects/${id}`, { method: 'DELETE' })
  if (!r.ok) throw new Error(`${r.status} ${r.statusText}`)
}

export async function uploadParts(id: string, files: File[]): Promise<{ saved: string[] }> {
  const fd = new FormData()
  for (const f of files) fd.append('files', f)
  const r = await fetch(`/api/projects/${id}/parts`, { method: 'POST', body: fd })
  if (!r.ok) throw new Error(`${r.status} ${r.statusText}`)
  return r.json()
}

export async function deletePart(id: string, filename: string): Promise<void> {
  const r = await fetch(`/api/projects/${id}/parts/${encodeURIComponent(filename)}`, {
    method: 'DELETE',
  })
  if (!r.ok) throw new Error(`${r.status} ${r.statusText}`)
}

export async function runProject(id: string): Promise<ProjectRunResult> {
  const r = await fetch(`/api/projects/${id}/generate`, { method: 'POST' })
  if (!r.ok) throw new Error(`${r.status} ${r.statusText}`)
  return r.json()
}


export async function importToolStep(opts: {
  file: File
  tool_id: string
  vendor: string
  part: string
  description: string
  stickout_mm: number
  flip_x_180: boolean
  mass_kg: number | null
}): Promise<ToolDefinition> {
  const fd = new FormData()
  fd.append('file', opts.file)
  fd.append('tool_id', opts.tool_id)
  fd.append('vendor', opts.vendor)
  fd.append('part', opts.part)
  fd.append('description', opts.description)
  fd.append('stickout_mm', String(opts.stickout_mm))
  fd.append('flip_x_180', String(opts.flip_x_180))
  if (opts.mass_kg != null) fd.append('mass_kg', String(opts.mass_kg))
  const res = await fetch('/api/tools/import', { method: 'POST', body: fd })
  if (!res.ok) {
    const body = await res.text()
    throw new Error(`import failed (${res.status}): ${body}`)
  }
  return (await res.json()) as ToolDefinition
}
